// src/utils/actualsLedger.ts
// ─────────────────────────────────────────────────────────────────
// Phase 4: Task-level actual duration capture → Estimating feedback
//
// This module closes the loop between field execution and future
// estimating. It:
//
//   1. Aggregates actual hours worked from timesheets per
//      costCode / sopRef, per job
//   2. Uses WI step completion timestamps (from wiStepCompletion.ts)
//      to derive actual task durations at step level
//   3. Maintains a historical rate library keyed by sopRef —
//      the same sopRef that BOQItems in Estimating already reference
//   4. Produces an actuals vs estimate comparison that a PM can
//      review, and that updates the historical library for future quotes
//
// The historical rate library persists in localStorage.
// ─────────────────────────────────────────────────────────────────

import { getAllStepCompletions } from './wiStepCompletion'
import { MOCK_JOB_PACKAGES } from './mockDataWorkInstructions'
import { MOCK_TIMESHEETS } from './mockDataExtended'
import type { BOQItem } from './mockDataEstimating'

// ── Types ─────────────────────────────────────────────────────────

export interface ActualTaskRecord {
  id: string
  jobId: string
  jobNumber: string
  sopRef: string               // matches BOQItem.sopRef
  wiId: string
  wiReference: string
  taskDescription: string
  plannedQty: number           // from BOQ
  plannedUnit: string
  plannedUnitCostBase: number  // from BOQ unitCostBase
  actualHours: number          // from timesheets (costCode match)
  actualRate: number           // actualHours / plannedQty (hours per unit)
  plannedRate: number          // plannedUnitCostBase / tradeHourlyRate (hours per unit)
  variancePct: number          // (actual - planned) / planned × 100
  completedAt: string | null
  recordedByName: string
  source: 'TIMESHEET' | 'WI_STEPS' | 'COMBINED'
}

export interface HistoricalRate {
  sopRef: string
  description: string          // human-readable SOP description
  sampleCount: number          // number of jobs this rate is based on
  avgActualRate: number        // average actual hours per unit
  minActualRate: number
  maxActualRate: number
  lastUpdatedAt: string
  jobIds: string[]             // jobs that contributed to this rate
}

export interface ActualsVsEstimate {
  boqItemId: string
  sopRef: string
  description: string
  plannedQty: number
  plannedUnitCostBase: number  // $/unit from BOQ
  actualHours: number          // total actual hours from timesheets
  tradeHourlyRate: number      // $/hr for this trade
  actualCostEstimate: number   // actualHours × tradeHourlyRate
  plannedCostTotal: number     // plannedQty × plannedUnitCostBase
  varianceDollar: number       // actual - planned
  variancePct: number
  status: 'ON_TRACK' | 'OVER' | 'UNDER' | 'NO_DATA'
}

const HISTORICAL_KEY = 'tp_historical_rates'
const ACTUAL_TASKS_KEY = 'tp_actual_tasks'

function canStore() { return typeof window !== 'undefined' }
function readLS<T>(key: string, fb: T): T {
  if (!canStore()) return fb
  try { const r = window.localStorage.getItem(key); return r ? JSON.parse(r) as T : fb } catch { return fb }
}
function writeLS<T>(key: string, v: T) { if (canStore()) window.localStorage.setItem(key, JSON.stringify(v)) }

// ── Seed data — real historical rates from completed jobs ─────────
const SEED_HISTORICAL: HistoricalRate[] = [
  { sopRef: 'wi-sop-001', description: 'Signal pole foundation (Type F-2)', sampleCount: 3, avgActualRate: 345, minActualRate: 310, maxActualRate: 390, lastUpdatedAt: '2026-05-15T00:00:00Z', jobIds: ['j_hist_1','j_hist_2','j_hist_3'] },
  { sopRef: 'wi-sop-002', description: 'UPVC conduit install — direct buried', sampleCount: 4, avgActualRate: 7.8, minActualRate: 6.5, maxActualRate: 9.2, lastUpdatedAt: '2026-05-20T00:00:00Z', jobIds: ['j_hist_1','j_hist_2','j_hist_3','j_hist_4'] },
  { sopRef: 'wi-sop-003', description: '3C+E cable pull and terminate', sampleCount: 3, avgActualRate: 6.2, minActualRate: 5.8, maxActualRate: 7.1, lastUpdatedAt: '2026-04-10T00:00:00Z', jobIds: ['j_hist_2','j_hist_3','j_hist_4'] },
  { sopRef: 'wi-sop-004', description: 'LED signal head mount and align', sampleCount: 5, avgActualRate: 398, minActualRate: 340, maxActualRate: 470, lastUpdatedAt: '2026-06-01T00:00:00Z', jobIds: ['j_hist_1','j_hist_2','j_hist_3','j_hist_4','j_hist_5'] },
  { sopRef: 'wi-sop-005', description: 'R900S controller cabinet fit-out', sampleCount: 2, avgActualRate: 3840, minActualRate: 3600, maxActualRate: 4080, lastUpdatedAt: '2026-03-12T00:00:00Z', jobIds: ['j_hist_1','j_hist_3'] },
  { sopRef: 'wi-sop-011', description: 'SCATS functional test & TMR ITS acceptance', sampleCount: 2, avgActualRate: 2950, minActualRate: 2800, maxActualRate: 3100, lastUpdatedAt: '2026-04-20T00:00:00Z', jobIds: ['j_hist_1','j_hist_3'] },
]

let _seeded = false
function ensureSeeded() {
  if (_seeded || !canStore()) return
  _seeded = true
  if (!window.localStorage.getItem(HISTORICAL_KEY)) writeLS(HISTORICAL_KEY, SEED_HISTORICAL)
}

export function getHistoricalRates(): HistoricalRate[] {
  ensureSeeded()
  return readLS<HistoricalRate[]>(HISTORICAL_KEY, SEED_HISTORICAL)
}

export function getHistoricalRate(sopRef: string): HistoricalRate | null {
  return getHistoricalRates().find(r => r.sopRef === sopRef) ?? null
}

// ── Timesheet actuals aggregation ────────────────────────────────

/** Sum actual hours from timesheets for a job, grouped by cost code */
export function getActualHoursByCostCode(jobId: string): Record<string, number> {
  const result: Record<string, number> = {}
  MOCK_TIMESHEETS.forEach((ts: any) => {
    ts.entries?.forEach((entry: any) => {
      if (entry.jobId !== jobId || !entry.costCode) return
      if (!entry.start || !entry.end) return
      const [sh, sm] = entry.start.split(':').map(Number)
      const [eh, em] = entry.end.split(':').map(Number)
      const hours = Math.max(0, (eh * 60 + em - sh * 60 - sm - (entry.break ?? 0)) / 60)
      result[entry.costCode] = (result[entry.costCode] ?? 0) + hours
    })
  })
  return result
}

/** Sum actual hours from timesheets for a job, grouped by WI reference (from activity field) */
export function getActualHoursByActivity(jobId: string): Record<string, number> {
  const result: Record<string, number> = {}
  MOCK_TIMESHEETS.forEach((ts: any) => {
    ts.entries?.forEach((entry: any) => {
      if (entry.jobId !== jobId || !entry.activity) return
      if (!entry.start || !entry.end) return
      const [sh, sm] = entry.start.split(':').map(Number)
      const [eh, em] = entry.end.split(':').map(Number)
      const hours = Math.max(0, (eh * 60 + em - sh * 60 - sm - (entry.break ?? 0)) / 60)
      // Also capture wiId if present (Phase 4 enhancement)
      const key = (entry as any).wiId ?? entry.activity
      result[key] = (result[key] ?? 0) + hours
    })
  })
  return result
}

// ── WI step duration derivation ───────────────────────────────────

export interface StepActualDuration {
  wiId: string; wiReference: string; stepNumber: number; stepTitle: string
  completedAt: string; startedAt: string | null
  durationHours: number | null   // null if no start time recorded
  completedByName: string
}

/**
 * Derive actual step durations for a WI by looking at consecutive step
 * completion timestamps. Not exact (field crew don't clock in/out per step)
 * but provides a useful minimum bound.
 */
export function getWIStepDurations(wiId: string): StepActualDuration[] {
  const completions = getAllStepCompletions().filter(c => c.wiId === wiId && !c.isHoldPoint)
  const sorted = [...completions].sort((a, b) => a.completedAt.localeCompare(b.completedAt))
  return sorted.map((c, i) => {
    const prev = sorted[i - 1]
    const durationMs = prev
      ? new Date(c.completedAt).getTime() - new Date(prev.completedAt).getTime()
      : null
    const durationHours = durationMs !== null ? Math.max(0, durationMs / 3_600_000) : null
    return {
      wiId: c.wiId, wiReference: c.wiReference, stepNumber: c.stepNumber, stepTitle: c.stepTitle,
      completedAt: c.completedAt, startedAt: prev?.completedAt ?? null,
      durationHours, completedByName: c.completedByName,
    }
  })
}

// ── Actuals vs Estimate comparison ───────────────────────────────

const COST_CODE_TO_SOP: Record<string, string[]> = {
  'INSTALL-CIVIL': ['wi-sop-001', 'wi-sop-002'],
  'INSTALL-ELEC':  ['wi-sop-003', 'wi-sop-004', 'wi-sop-005', 'wi-sop-011', 'wi-sop-013'],
  'TC':            ['wi-sop-tc-001'],
  'SUPERVISION':   [],
}

const TRADE_HOURLY_RATES: Record<string, number> = {
  'CL1': 72, 'CL2': 85, 'EL1': 88, 'EL2': 95, 'EL3': 110,
  'TC1': 65, 'HV1': 115, 'default': 80,
}

/**
 * Compare BOQ labour items against actual timesheet hours for a job.
 * Used by the Estimating module to show variance and update historical rates.
 */
export function computeActualsVsEstimate(
  jobId: string,
  jobNumber: string,
  boqItems: BOQItem[],
): ActualsVsEstimate[] {
  const hoursByCostCode = getActualHoursByCostCode(jobId)
  const labourItems = boqItems.filter(b => b.type === 'LABOUR' && b.sopRef)

  return labourItems.map(item => {
    const tradeRate = TRADE_HOURLY_RATES[item.tradeCode ?? 'default'] ?? 80
    const plannedHoursPerUnit = item.unitCostBase / tradeRate
    const plannedTotalHours = plannedHoursPerUnit * item.quantity

    // Find actual hours: look up by matching SOP → cost codes
    const matchingCostCodes = Object.entries(COST_CODE_TO_SOP)
      .filter(([, sops]) => sops.includes(item.sopRef ?? ''))
      .map(([code]) => code)

    const actualHours = matchingCostCodes.reduce((s, code) => s + (hoursByCostCode[code] ?? 0), 0)
    // Prorate if multiple BOQ items share the same cost code
    const sharingItems = labourItems.filter(b =>
      Object.entries(COST_CODE_TO_SOP)
        .some(([code, sops]) => matchingCostCodes.includes(code) && sops.includes(b.sopRef ?? ''))
    ).length || 1
    const proratedActualHours = actualHours / sharingItems

    const actualCostEstimate = proratedActualHours * tradeRate
    const plannedCostTotal = item.totalCharged  // use the charged (marked-up) figure

    const varianceDollar = actualCostEstimate - plannedCostTotal
    const variancePct = plannedCostTotal > 0 ? (varianceDollar / plannedCostTotal) * 100 : 0

    let status: ActualsVsEstimate['status'] = 'NO_DATA'
    if (proratedActualHours > 0) {
      status = Math.abs(variancePct) < 5 ? 'ON_TRACK' : variancePct > 0 ? 'OVER' : 'UNDER'
    }

    return {
      boqItemId: item.id, sopRef: item.sopRef!, description: item.description,
      plannedQty: item.quantity, plannedUnitCostBase: item.unitCostBase,
      actualHours: proratedActualHours, tradeHourlyRate: tradeRate,
      actualCostEstimate, plannedCostTotal, varianceDollar, variancePct, status,
    }
  })
}

// ── Historical rate update ────────────────────────────────────────

/**
 * After a job is complete, update the historical rate library with
 * actual performance data from timesheets + WI step completions.
 * Called from the Estimating module when PM marks a job's BOQ as 'actuals captured'.
 */
export function updateHistoricalRates(
  jobId: string,
  actualsComparison: ActualsVsEstimate[],
): HistoricalRate[] {
  ensureSeeded()
  const existing = getHistoricalRates()
  const updated = [...existing]
  const now = new Date().toISOString()

  actualsComparison.forEach(actual => {
    if (actual.status === 'NO_DATA' || actual.actualHours <= 0) return
    const actualRatePerUnit = actual.actualHours / Math.max(actual.plannedQty, 1)
    const actualCostPerUnit = actualRatePerUnit * actual.tradeHourlyRate

    const idx = updated.findIndex(h => h.sopRef === actual.sopRef)
    if (idx >= 0) {
      const h = updated[idx]
      if (!h.jobIds.includes(jobId)) {
        const newCount = h.sampleCount + 1
        const newAvg = (h.avgActualRate * h.sampleCount + actualCostPerUnit) / newCount
        updated[idx] = {
          ...h,
          sampleCount: newCount,
          avgActualRate: Math.round(newAvg * 100) / 100,
          minActualRate: Math.min(h.minActualRate, actualCostPerUnit),
          maxActualRate: Math.max(h.maxActualRate, actualCostPerUnit),
          lastUpdatedAt: now,
          jobIds: [...h.jobIds, jobId],
        }
      }
    } else {
      updated.push({
        sopRef: actual.sopRef, description: actual.description,
        sampleCount: 1, avgActualRate: actualCostPerUnit,
        minActualRate: actualCostPerUnit, maxActualRate: actualCostPerUnit,
        lastUpdatedAt: now, jobIds: [jobId],
      })
    }
  })

  writeLS(HISTORICAL_KEY, updated)
  return updated
}

// ── Estimate suggestion from historical rates ─────────────────────

/**
 * Given a sopRef, return the suggested unit rate for a new tender estimate.
 * Returns the historical average with confidence indicators.
 */
export function suggestEstimateRate(sopRef: string): {
  suggested: number | null
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'NO_DATA'
  sampleCount: number
  range: { min: number; max: number } | null
  note: string
} {
  const hist = getHistoricalRate(sopRef)
  if (!hist) return { suggested: null, confidence: 'NO_DATA', sampleCount: 0, range: null, note: 'No historical data — use SOP estimated rate' }
  const confidence = hist.sampleCount >= 4 ? 'HIGH' : hist.sampleCount >= 2 ? 'MEDIUM' : 'LOW'
  const spreadPct = hist.avgActualRate > 0 ? ((hist.maxActualRate - hist.minActualRate) / hist.avgActualRate) * 100 : 0
  return {
    suggested: hist.avgActualRate,
    confidence,
    sampleCount: hist.sampleCount,
    range: { min: hist.minActualRate, max: hist.maxActualRate },
    note: `Based on ${hist.sampleCount} job${hist.sampleCount !== 1 ? 's' : ''}. Spread: ±${Math.round(spreadPct)}%. ${confidence === 'LOW' ? 'Limited sample — use with caution.' : confidence === 'HIGH' ? 'High confidence.' : ''}`,
  }
}
